import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Save,
  User,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const SalaryFormPage = () => {
  const navigate = useNavigate();
  const { salaryId } = useParams();
  const { hasPermission } = useAdminAuth();
  const isEditMode = Boolean(salaryId);

  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    employeeId: '',
    month: '',
    year: new Date().getFullYear(),
    basicSalary: '',
    housingAllowance: '',
    transportAllowance: '',
    otherAllowances: '',
    bonuses: '',
    deductions: '',
    status: 'draft',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Mock employees data
  useEffect(() => {
    const mockEmployees = [
      { id: 'EMP-001', name: 'أحمد محمد', employeeNumber: '1001', position: 'محامي أول', basicSalary: 15000 },
      { id: 'EMP-002', name: 'فاطمة علي', employeeNumber: '1002', position: 'محاسبة', basicSalary: 12000 },
      { id: 'EMP-003', name: 'خالد سالم', employeeNumber: '1003', position: 'مساعد إداري', basicSalary: 8000 },
      { id: 'EMP-004', name: 'سارة محمود', employeeNumber: '1004', position: 'سكرتيرة تنفيذية', basicSalary: 9000 },
      { id: 'EMP-005', name: 'محمد عبدالله', employeeNumber: '1005', position: 'محامي', basicSalary: 13000 }
    ];
    setEmployees(mockEmployees);

    if (isEditMode) {
      // Load existing salary data
      setTimeout(() => {
        setFormData({
          employeeId: 'EMP-001',
          month: '12',
          year: 2024,
          basicSalary: '15000',
          housingAllowance: '3000',
          transportAllowance: '1000',
          otherAllowances: '500',
          bonuses: '2000',
          deductions: '500',
          status: 'ready',
          notes: ''
        });
      }, 300);
    }
  }, [isEditMode]);

  const handleEmployeeChange = (e) => {
    const empId = e.target.value;
    const employee = employees.find(emp => emp.id === empId);
    
    setFormData(prev => ({
      ...prev,
      employeeId: empId,
      basicSalary: employee ? employee.basicSalary.toString() : ''
    }));
    
    setSelectedEmployee(employee);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const basic = parseFloat(formData.basicSalary) || 0;
    const housing = parseFloat(formData.housingAllowance) || 0;
    const transport = parseFloat(formData.transportAllowance) || 0;
    const other = parseFloat(formData.otherAllowances) || 0;
    const bonuses = parseFloat(formData.bonuses) || 0;
    const deductions = parseFloat(formData.deductions) || 0;

    const totalEarnings = basic + housing + transport + other + bonuses;
    const netSalary = totalEarnings - deductions;

    return { totalEarnings, totalDeductions: deductions, netSalary };
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.employeeId) {
      newErrors.employeeId = 'يرجى اختيار الموظف';
    }
    if (!formData.month) {
      newErrors.month = 'يرجى تحديد الشهر';
    }
    if (!formData.basicSalary || parseFloat(formData.basicSalary) <= 0) {
      newErrors.basicSalary = 'الراتب الأساسي مطلوب';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (action) => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Determine final status based on action
    let finalStatus = formData.status;
    if (action === 'draft') {
      finalStatus = 'draft';
    } else if (action === 'approve' && hasPermission('finance.salary.approve')) {
      finalStatus = 'approved';
    } else if (action === 'save') {
      finalStatus = 'ready';
    }

    setTimeout(() => {
      console.log('Salary Data:', { ...formData, status: finalStatus });
      alert(isEditMode ? 'تم تحديث الراتب بنجاح' : 'تم إنشاء الراتب بنجاح');
      navigate('/admin/finance/salaries');
      setLoading(false);
    }, 1000);
  };

  const totals = calculateTotals();

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/salaries')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للرواتب</span>
          </button>

          <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">
            {isEditMode ? 'تعديل راتب' : 'إنشاء راتب جديد'}
          </h1>
          <p className="text-gray-600">إدخال البنود المالية مع حساب تلقائي</p>
        </div>

        <form className="space-y-6">
          {/* Employee Selection */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#1E3A8A]" />
              اختيار الموظف
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الموظف <span className="text-red-500">*</span>
                </label>
                <select
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleEmployeeChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.employeeId ? 'border-red-300' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  disabled={isEditMode}
                >
                  <option value="">-- اختر الموظف --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} - {emp.employeeNumber} - {emp.position}
                    </option>
                  ))}
                </select>
                {errors.employeeId && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.employeeId}
                  </p>
                )}
              </div>

              {selectedEmployee && (
                <div className="md:col-span-2 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">الرقم الوظيفي:</span>
                      <p className="font-bold text-gray-800">{selectedEmployee.employeeNumber}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">المسمى:</span>
                      <p className="font-bold text-gray-800">{selectedEmployee.position}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">الراتب الأساسي:</span>
                      <p className="font-bold text-gray-800">{selectedEmployee.basicSalary.toLocaleString()} ر.س</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Month Selection */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#1E3A8A]" />
              تحديد الشهر
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الشهر <span className="text-red-500">*</span>
                </label>
                <select
                  name="month"
                  value={formData.month}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.month ? 'border-red-300' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                >
                  <option value="">-- اختر الشهر --</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                {errors.month && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.month}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  السنة
                </label>
                <select
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                </select>
              </div>
            </div>
          </div>

          {/* Financial Items */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#1E3A8A]" />
              البنود المالية
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الراتب الأساسي <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="basicSalary"
                  value={formData.basicSalary}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none ${
                    errors.basicSalary ? 'border-red-300' : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">بدل السكن</label>
                <input
                  type="number"
                  name="housingAllowance"
                  value={formData.housingAllowance}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">بدل النقل</label>
                <input
                  type="number"
                  name="transportAllowance"
                  value={formData.transportAllowance}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">بدلات أخرى</label>
                <input
                  type="number"
                  name="otherAllowances"
                  value={formData.otherAllowances}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">المكافآت</label>
                <input
                  type="number"
                  name="bonuses"
                  value={formData.bonuses}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">الخصومات</label>
                <input
                  type="number"
                  name="deductions"
                  value={formData.deductions}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Auto Calculate */}
          <div className="bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] rounded-lg p-6 text-white">
            <h3 className="text-lg font-black mb-4">الحساب التلقائي</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm opacity-90 mb-1">إجمالي الاستحقاقات</p>
                <p className="text-2xl font-black">{totals.totalEarnings.toLocaleString()} ر.س</p>
              </div>
              <div>
                <p className="text-sm opacity-90 mb-1">إجمالي الخصومات</p>
                <p className="text-2xl font-black">{totals.totalDeductions.toLocaleString()} ر.س</p>
              </div>
              <div>
                <p className="text-sm opacity-90 mb-1">صافي الراتب</p>
                <p className="text-3xl font-black">{totals.netSalary.toLocaleString()} ر.س</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">ملاحظات</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              rows="3"
              placeholder="أي ملاحظات إضافية..."
            ></textarea>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleSubmit('save')}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              <span>حفظ</span>
            </button>

            <button
              type="button"
              onClick={() => handleSubmit('draft')}
              disabled={loading}
              className="px-6 py-3 border-2 border-gray-400 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
            >
              حفظ كمسودة
            </button>

            {hasPermission('finance.salary.approve') && (
              <button
                type="button"
                onClick={() => handleSubmit('approve')}
                disabled={loading}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
              >
                <CheckCircle className="w-5 h-5" />
                <span>حفظ واعتماد</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/admin/finance/salaries')}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 font-bold transition-colors"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default SalaryFormPage;